import React from 'react';
import { Wifi, Shield, Lock, Server } from 'lucide-react';

export default function DeviceConnectionFields({ value = {}, onChange, brand = 'Hikvision', isEditing = false }) {
    const v = {
        ipAddress: value.ipAddress ?? '',
        port: value.port ?? (brand === 'Suprema' ? 51211 : brand === 'Xpass' ? 5555 : value.useHttps ? 443 : 80),
        useHttps: !!value.useHttps,
        username: value.username ?? '',
        password: value.password ?? '',
        protocol: value.protocol ?? (brand === 'Suprema' ? 'SDK' : brand === 'Xpass' ? 'UDP' : 'ISAPI'),
        sdkPort: value.sdkPort ?? 8000,
        verifyCertificate: !!value.verifyCertificate,
    };

    const set = (patch) => onChange?.({ ...v, ...patch });
    const normBrand = (brand || '').trim();
    const isHik = normBrand.toLowerCase().includes('hik');
    const isSuprema = normBrand.toLowerCase() === 'suprema';
    const isXpass = normBrand.toLowerCase() === 'xpass' || normBrand.toLowerCase() === 'orion';

    const onToggleHttps = (checked) => {
        const currentPort = Number(v.port);
        let nextPort = currentPort;
        if (checked && (!currentPort || currentPort === 80)) {
            nextPort = 443;
        } else if (!checked && currentPort === 443) {
            nextPort = 80;
        }
        set({
            useHttps: checked,
            port: nextPort,
            verifyCertificate: checked ? v.verifyCertificate : false,
        });
    };

    const inputClass =
        'w-full px-3 py-1.5 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500 outline-none transition-all';
    const labelClass = 'block text-[11px] font-bold uppercase tracking-wider text-gray-600 dark:text-gray-300 mb-1';

    if (isSuprema) {
        return (
            <div className="p-3.5 rounded-xl border border-gray-200 dark:border-gray-700/80 bg-gray-50/70 dark:bg-gray-900/40 space-y-2.5">
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-indigo-600 dark:text-indigo-400 flex items-center gap-1.5">
                        <Wifi className="w-3.5 h-3.5" />
                        Suprema Ağ &amp; SDK Bağlantısı
                    </h4>
                    <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-sky-100 text-sky-800 dark:bg-sky-900/50 dark:text-sky-200">
                        TCP / BioStar SDK (Şifresiz)
                    </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="sm:col-span-2">
                        <label className={labelClass}>
                            Cihaz IP Adresi <span className="text-rose-500">*</span>
                        </label>
                        <input
                            type="text"
                            className={inputClass}
                            placeholder="Örn: 192.168.1.120"
                            value={v.ipAddress}
                            onChange={(e) => set({ ipAddress: e.target.value })}
                        />
                    </div>
                    <div>
                        <label className={labelClass}>SDK Portu</label>
                        <input
                            type="number"
                            className={inputClass}
                            min={1}
                            max={65535}
                            placeholder="51211"
                            value={v.port}
                            onChange={(e) => {
                                const val = e.target.value;
                                set({ port: val === '' ? '' : Number(val), protocol: 'SDK' });
                            }}
                        />
                    </div>
                </div>
            </div>
        );
    }

    if (isXpass) {
        return (
            <div className="p-3.5 rounded-xl border border-gray-200 dark:border-gray-700/80 bg-gray-50/70 dark:bg-gray-900/40 space-y-2.5">
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-indigo-600 dark:text-indigo-400 flex items-center gap-1.5">
                        <Wifi className="w-3.5 h-3.5" />
                        Orion (Xpass) Ağ Bağlantısı
                    </h4>
                    <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-200">
                        UDP / Gateway (Şifresiz)
                    </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="sm:col-span-2">
                        <label className={labelClass}>Cihaz IP Adresi (Opsiyonel)</label>
                        <input
                            type="text"
                            className={inputClass}
                            placeholder="Örn: 192.168.1.150 (DHCP ise boş bırakılabilir)"
                            value={v.ipAddress}
                            onChange={(e) => set({ ipAddress: e.target.value })}
                        />
                    </div>
                    <div>
                        <label className={labelClass}>UDP Portu</label>
                        <input
                            type="number"
                            className={inputClass}
                            min={1}
                            max={65535}
                            placeholder="5555"
                            value={v.port}
                            onChange={(e) => {
                                const val = e.target.value;
                                set({ port: val === '' ? '' : Number(val), protocol: 'UDP' });
                            }}
                        />
                    </div>
                </div>
            </div>
        );
    }

    const isSdkMode = v.protocol === 'SDK';

    return (
        <div className="p-3.5 rounded-xl border border-gray-200 dark:border-gray-700/80 bg-gray-50/70 dark:bg-gray-900/40 space-y-2.5">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <h4 className="text-xs font-bold uppercase tracking-wider text-indigo-600 dark:text-indigo-400 flex items-center gap-1.5">
                    <Server className="w-3.5 h-3.5" />
                    {isHik ? 'Hikvision Bağlantı & Kimlik Ayarları' : 'Ağ & Bağlantı Ayarları'}
                </h4>

                <div className="inline-flex p-0.5 rounded-lg bg-gray-200/80 dark:bg-gray-800 border border-gray-300/60 dark:border-gray-700">
                    <button
                        type="button"
                        onClick={() => set({ protocol: 'ISAPI' })}
                        className={`px-2.5 py-1 rounded-md text-[11px] font-bold transition-all ${
                            !isSdkMode
                                ? 'bg-indigo-600 text-white shadow-sm'
                                : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                        }`}
                    >
                        ISAPI (HTTP/S)
                    </button>
                    <button
                        type="button"
                        onClick={() => set({ protocol: 'SDK', sdkPort: v.sdkPort || 8000 })}
                        className={`px-2.5 py-1 rounded-md text-[11px] font-bold transition-all ${
                            isSdkMode
                                ? 'bg-indigo-600 text-white shadow-sm'
                                : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                        }`}
                    >
                        HCNetSDK (8000)
                    </button>
                </div>
            </div>

            <div className={`grid grid-cols-1 ${isSdkMode ? 'sm:grid-cols-4' : 'sm:grid-cols-3'} gap-2.5`}>
                <div className="sm:col-span-2">
                    <label className={labelClass}>
                        Cihaz IP Adresi <span className="text-rose-500">*</span>
                    </label>
                    <input
                        type="text"
                        className={inputClass}
                        placeholder="Örn: 192.168.1.64"
                        value={v.ipAddress}
                        onChange={(e) => set({ ipAddress: e.target.value })}
                    />
                </div>

                {isSdkMode && (
                    <div>
                        <label className={labelClass}>
                            SDK Port <span className="text-rose-500">*</span>
                        </label>
                        <input
                            type="number"
                            className={inputClass}
                            min={1}
                            max={65535}
                            placeholder="8000"
                            value={v.sdkPort}
                            onChange={(e) => {
                                const val = e.target.value;
                                set({ sdkPort: val === '' ? '' : Number(val) });
                            }}
                        />
                    </div>
                )}

                <div>
                    <label className={labelClass}>{isSdkMode ? 'Web Port' : 'HTTP/S Port'}</label>
                    <input
                        type="number"
                        className={inputClass}
                        min={1}
                        max={65535}
                        placeholder={v.useHttps ? '443' : '80'}
                        value={v.port}
                        onChange={(e) => {
                            const val = e.target.value;
                            set({ port: val === '' ? '' : Number(val) });
                        }}
                    />
                </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                <div>
                    <label className={labelClass}>
                        Kullanıcı Adı <span className="text-rose-500">*</span>
                    </label>
                    <input
                        type="text"
                        className={inputClass}
                        placeholder="admin"
                        value={v.username}
                        onChange={(e) => set({ username: e.target.value })}
                    />
                </div>
                <div>
                    <label className={labelClass}>
                        Cihaz Şifresi {!isEditing && <span className="text-rose-500">*</span>}
                    </label>
                    <input
                        type="password"
                        className={inputClass}
                        placeholder={isEditing ? 'Değiştirmek için yazın' : 'Cihaz şifresi'}
                        value={v.password}
                        onChange={(e) => set({ password: e.target.value })}
                        autoComplete="new-password"
                    />
                </div>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3 pt-0.5">
                <div className="flex flex-wrap items-center gap-4">
                    <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                        <input
                            type="checkbox"
                            className="w-3.5 h-3.5 rounded text-indigo-600 focus:ring-indigo-500 border-gray-300 dark:border-gray-600 dark:bg-gray-700"
                            checked={v.useHttps}
                            onChange={(e) => onToggleHttps(e.target.checked)}
                        />
                        <span className="text-xs font-semibold text-gray-700 dark:text-gray-200 flex items-center gap-1">
                            <Lock className="w-3 h-3 text-emerald-500" />
                            HTTPS Kullan
                        </span>
                    </label>

                    {v.useHttps && (
                        <label
                            className="inline-flex items-center gap-1.5 cursor-pointer select-none"
                            title="Self-signed (cihazın kendi ürettiği) sertifikalarda kapalı bırakın"
                        >
                            <input
                                type="checkbox"
                                className="w-3.5 h-3.5 rounded text-indigo-600 focus:ring-indigo-500 border-gray-300 dark:border-gray-600 dark:bg-gray-700"
                                checked={v.verifyCertificate}
                                onChange={(e) => set({ verifyCertificate: e.target.checked })}
                            />
                            <span className="text-xs font-medium text-gray-600 dark:text-gray-300 flex items-center gap-1">
                                <Shield className="w-3 h-3 text-indigo-500" />
                                SSL Doğrula
                            </span>
                        </label>
                    )}
                </div>

                {isEditing && (
                    <span className="text-[11px] text-gray-500 dark:text-gray-400">
                        Kayıtlı şifre test/bilgi çekmede otomatik kullanılır.
                    </span>
                )}
            </div>
        </div>
    );
}
